import { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Modal, Platform, StyleSheet, Text, View } from 'react-native';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

export type AppAlertButtonStyle = 'default' | 'cancel' | 'destructive';

export type AppAlertButton = {
  text: string;
  onPress?: () => void;
  style?: AppAlertButtonStyle;
};

type AlertState = {
  title: string;
  message?: string;
  buttons: AppAlertButton[];
};

let showRef: ((state: AlertState) => void) | null = null;

/**
 * Drop-in, θεματισμένη αντικατάσταση του `Alert.alert(title, message, buttons)`
 * της React Native — ίδιο API, αλλά renders modal styled με τα χρώματα της
 * εφαρμογής αντί για το native OS dialog (γκρι σε Android, λευκό σε iOS).
 * Χωρίς buttons, δείχνει ένα προεπιλεγμένο "ΟΚ".
 */
export function appAlert(title: string, message?: string, buttons?: AppAlertButton[]): void {
  const resolved = buttons && buttons.length > 0 ? buttons : [{ text: 'OK', style: 'default' as const }];
  if (!showRef) return;
  showRef({ title, message, buttons: resolved });
}

export function AppAlertProvider({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [state, setState] = useState<AlertState | null>(null);
  const scale = useRef(new Animated.Value(0.9)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    showRef = (next) => {
      setState(next);
      scale.setValue(0.9);
      opacity.setValue(0);
      Animated.parallel([
        Animated.spring(scale, { toValue: 1, useNativeDriver: Platform.OS !== 'web', friction: 7, tension: 90 }),
        Animated.timing(opacity, { toValue: 1, duration: 160, useNativeDriver: Platform.OS !== 'web' }),
      ]).start();
    };
    return () => { showRef = null; };
  }, [scale, opacity]);

  function handlePress(btn: AppAlertButton) {
    setState(null);
    btn.onPress?.();
  }

  function handleDismiss() {
    setState(null);
  }

  return (
    <>
      {children}
      {!!state && (
        <Modal transparent visible animationType="fade" onRequestClose={handleDismiss}>
          <View style={styles.overlay}>
            <Animated.View style={[styles.card, { opacity, transform: [{ scale }] }]}>
              <Text style={styles.title}>{state.title}</Text>
              {!!state.message && <Text style={styles.message}>{state.message}</Text>}
              <View style={styles.actions}>
                {state.buttons.map((btn, i) => (
                  <Button
                    key={i}
                    title={btn.text}
                    variant={btn.style === 'cancel' ? 'secondary' : 'primary'}
                    danger={btn.style === 'destructive'}
                    onPress={() => handlePress(btn)}
                  />
                ))}
              </View>
            </Animated.View>
          </View>
        </Modal>
      )}
    </>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.6)',
      padding: spacing.lg,
    },
    card: {
      width: '100%',
      maxWidth: 420,
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.lg,
      gap: spacing.md,
    },
    title: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    message: {
      color: colors.text,
      fontSize: fontSize.md,
      lineHeight: 22,
    },
    actions: {
      gap: spacing.sm,
      marginTop: spacing.xs,
    },
  });
}
